import React, { FC, Fragment, Suspense, useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Tab } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import SupplierOrders from './supplier-orders/supplier-orders.component';
import { Order } from 'models/order';
import SupplierDirectory from './supplier-directory/supplier-directory.component';
import OutgoingSupplierRequests from 'components/supplier-requests/outgoing-supplier-requests.component';
import SupplierBrowser from 'components/supplier-browser/supplier-browser.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

interface CheckedOrder extends Order {
    checked: boolean;
}

const Suppliers: FC = () => {
    const { t: translate } = useTranslation(['orders', 'client-directory']);
    const { tab } = useParams<{ tab: string }>();
    const [tabState, setTabState] = useState(tab || 'supplier-orders');
    const navigate = useNavigate();
    // DD-3662: choosing suppliers is the lab's own business; the backend refuses external users too.
    const canBrowseSuppliers = !permissions.isExternalTechnician(currentUserVar()?.role);

    const handleTabChange = (_: unknown, newTabState: string) => {
        if (tabState !== newTabState) {
            navigate(`/suppliers/${newTabState}`);
            setTabState(newTabState);
        }
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('client-directory:SUPPLIERS')} />
            <TabContext value={tabState}>
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                    <TabList onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                        <Tab label={translate('orders:ORDERS')} value="supplier-orders" />
                        <Tab label={translate('client-directory:SUPPLIERS')} value="supplier-list" />
                        {canBrowseSuppliers ? (
                            <Tab
                                label={translate('client-directory:FIND_SUPPLIERS')}
                                value="supplier-browser"
                                data-test="suppliers.browser-tab"
                            />
                        ) : undefined}
                        <Tab
                            label={translate('client-directory:OUTGOING_SUPPLIER_REQUESTS')}
                            value="supplier-requests"
                            data-test="suppliers.requests-tab"
                        />
                    </TabList>
                    {/* Placing an order belongs to the order list, not the supplier directory. */}
                    {tabState === 'supplier-orders' ? (
                        <Button
                            variant="contained"
                            onClick={() => navigate('/orders/create/supplier')}
                            data-test="suppliers.new-supplier-order-btn"
                        >
                            {translate('client-directory:NEW_SUPPLIER_ORDER')}
                        </Button>
                    ) : undefined}
                </Box>
                <TabPanel sx={{ padding: 0 }} value="supplier-orders">
                    <Suspense fallback={null}>
                        <SupplierOrders />
                    </Suspense>
                </TabPanel>

                <TabPanel sx={{ padding: 0 }} value="supplier-list">
                    <Suspense fallback={null}>
                        <SupplierDirectory />
                    </Suspense>
                </TabPanel>

                {canBrowseSuppliers ? (
                    <TabPanel sx={{ padding: 0 }} value="supplier-browser">
                        <SupplierBrowser />
                    </TabPanel>
                ) : undefined}

                <TabPanel sx={{ padding: 0 }} value="supplier-requests">
                    <OutgoingSupplierRequests />
                </TabPanel>
            </TabContext>
        </Fragment>
    );
};

export default Suppliers;
